import type { JSX } from "react";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { getUser, clearToken } from "../lib/auth";
import { fetchHealth } from "../lib/api";
import { usePresence } from "../hooks/usePresence";
import { ConsoleProvider, useConsole, rangeOptions } from "./ConsoleContext";
import { BrandLogo } from "../components/BrandLogo";
import {
  IconGrid,
  IconChart,
  IconSettings,
  IconUsers,
  IconMessage,
  IconCopy,
  IconStop,
  IconFile,
  IconFlag,
  IconArrowRight
} from "../components/icons";

const NAV = [
  {
    to: "/admin/overview",
    label: "概览",
    over: "Overview",
    desc: "系统健康与关键指标一览",
    Icon: IconGrid
  },
  {
    to: "/admin/traffic",
    label: "流量",
    over: "Traffic",
    desc: "请求、延迟与接口分布",
    Icon: IconChart
  },
  {
    to: "/admin/insights",
    label: "洞察",
    over: "Insights",
    desc: "命中率、转人工、点踩与延迟趋势",
    Icon: IconChart
  },
  {
    to: "/admin/usage",
    label: "用量",
    over: "Usage",
    desc: "用户活跃、token、成本与缓存",
    Icon: IconChart
  },
  {
    to: "/admin/system",
    label: "系统",
    over: "System",
    desc: "主机资源与依赖服务状态",
    Icon: IconSettings
  },
  { to: "/admin/users", label: "用户", over: "Users", desc: "账户、活跃度与管理", Icon: IconUsers },
  {
    to: "/admin/activity",
    label: "活动",
    over: "Activity",
    desc: "会话、标注与事件流",
    Icon: IconMessage
  },
  {
    to: "/admin/product-feedback",
    label: "产品反馈",
    over: "Feedback",
    desc: "用户反馈列表、上下文与处置",
    Icon: IconFlag
  },
  {
    to: "/admin/backups",
    label: "备份",
    over: "Backups",
    desc: "数据库备份运行记录",
    Icon: IconCopy
  },
  {
    to: "/admin/errors",
    label: "错误",
    over: "Errors",
    desc: "错误率趋势与最近错误",
    Icon: IconStop
  },
  { to: "/admin/logs", label: "日志", over: "Logs", desc: "运行日志与版本记录", Icon: IconFile },
  {
    to: "/admin/deployments",
    label: "部署",
    over: "Deployments",
    desc: "部署历史与提交记录",
    Icon: IconFile
  },
  // The annotation console is a standalone reviewer route (admin∪agent), not an
  // /admin section — link out to it so admins have a console entry (阶段②).
  {
    to: "/review",
    label: "标注台",
    over: "Review",
    desc: "人工标注与 gold 种子产出",
    Icon: IconArrowRight
  }
];

/** Flag-gated (FEATURE_KB_ADMIN) — appended to NAV only when the health check
 * reports the feature on, so the entry stays dark otherwise. */
const KB_ENTRY = { to: "/admin/knowledge", label: "知识库", over: "Knowledge", desc: "来源、手动入库与按租户访问", Icon: IconFile } as const;

/** The effective nav: static NAV plus the KB entry when FEATURE_KB_ADMIN is on. */
function useNav(): typeof NAV {
  const [kbAdmin, setKbAdmin] = useState(false);
  useEffect(() => {
    let alive = true;
    void fetchHealth().then((h) => {
      if (alive && h.kind === "ok" && h.health.features?.kbAdmin === true) setKbAdmin(true);
    });
    return () => { alive = false; };
  }, []);
  // Insert 知识库 next to 备份/部署 (management cluster), before 标注台.
  return kbAdmin ? [...NAV.slice(0, NAV.length - 1), KB_ENTRY, NAV[NAV.length - 1]!] : NAV;
}

const MODE_KEY = "cyclecare_mode";

/** Light/dark toggle (data-mode on <html>), shared with the chat app. */
function ThemeToggle(): JSX.Element {
  const [mode, setMode] = useState<string>(
    () => document.documentElement.getAttribute("data-mode") ?? "dark"
  );
  const toggle = (): void => {
    const next = mode === "dark" ? "light" : "dark";
    document.documentElement.setAttribute("data-mode", next);
    localStorage.setItem(MODE_KEY, next);
    setMode(next);
  };
  return (
    <button
      className="cc-icon-btn"
      type="button"
      onClick={toggle}
      aria-label="切换明暗模式"
      title={mode === "dark" ? "切换到浅色" : "切换到深色"}
    >
      {mode === "dark" ? (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
      ) : (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        </svg>
      )}
    </button>
  );
}

/** Format an epoch ms as the value a `datetime-local` input expects. */
function toLocalInput(ms: number): string {
  const d = new Date(ms);
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Absolute from/to date-time picker (#228 Phase 3). The range presets stay as
 * quick buttons; this lets an admin pin an exact historical window. Seeded from
 * the current effective window so opening it never jumps to an empty state.
 */
function WindowPicker(): JSX.Element {
  const { win, setWin, effectiveWindow } = useConsole();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(() => toLocalInput(effectiveWindow.from));
  const [to, setTo] = useState(() => toLocalInput(effectiveWindow.to));
  const apply = (): void => {
    const f = new Date(from).getTime();
    const t = new Date(to).getTime();
    if (Number.isFinite(f) && Number.isFinite(t) && t > f) {
      setWin({ from: f, to: t });
      setOpen(false);
    }
  };
  return (
    <div className="cc-window-picker">
      <button
        type="button"
        className={`cc-range${win !== null ? " active" : ""}`}
        onClick={() => {
          setFrom(toLocalInput(effectiveWindow.from));
          setTo(toLocalInput(effectiveWindow.to));
          setOpen((o) => !o);
        }}
      >
        自定义
      </button>
      {open && (
        <div className="cc-window-pop">
          <label className="cc-window-field">
            起<input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="cc-window-field">
            止<input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <div className="cc-window-actions">
            {win !== null && (
              <button
                type="button"
                className="cc-back"
                onClick={() => {
                  setWin(null);
                  setOpen(false);
                }}
              >
                清除
              </button>
            )}
            <button type="button" className="cc-range active" onClick={apply}>
              应用
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Only these sections query over the topbar time window; elsewhere the range
// selector is a dead control, so it is hidden rather than shown-but-ignored
// (manual-test feedback #228). Keep this in sync with the sections that read
// `win`/`query` from ConsoleContext (traffic / errors / insights).
const RANGE_SECTIONS = new Set(["traffic", "errors", "insights", "usage"]);

function Topbar(): JSX.Element {
  const { range, setRange, win, live, setLive } = useConsole();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const section = pathname.split("/").filter(Boolean).pop() ?? "";
  const showRange = RANGE_SECTIONS.has(section);
  return (
    <div className="cc-topbar">
      <button className="cc-back" type="button" onClick={() => navigate("/")}>
        <IconArrowRight size={14} style={{ transform: "rotate(180deg)" }} />
        返回对话
      </button>
      <span className="cc-topbar-spacer" />
      {showRange && (
        <>
          <div className="cc-range-group">
            {rangeOptions.map((r) => (
              <button
                key={r.value}
                type="button"
                className={`cc-range${win === null && range === r.value ? " active" : ""}`}
                onClick={() => setRange(r.value)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <WindowPicker />
        </>
      )}
      <button
        className={`cc-live${live ? " on" : ""}`}
        type="button"
        onClick={() => setLive(!live)}
      >
        <span className="cc-live-dot" />
        实时
      </button>
      <ThemeToggle />
    </div>
  );
}

/** Editorial page header — the active section's title in Fraunces + a one-line
 * description, so each console page reads like a native Cycle page. */
function PageHead(): JSX.Element {
  const { pathname } = useLocation();
  const nav = useNav();
  // The flag-gated 助手配置 entry lives outside the static NAV (its rail link
  // is conditional), but the header lookup must still resolve its title when
  // the route is visited directly.
  const ASSISTANTS_ENTRY = {
    to: "/admin/assistants",
    label: "助手配置",
    over: "Assistants",
    desc: "助手的身份、说明与发布",
    Icon: IconUsers
  };
  const active =
    [...nav, ASSISTANTS_ENTRY].find((n) => pathname.startsWith(n.to)) ?? nav[0]!;
  return (
    <header className="cc-page-head">
      <span className="cc-page-overline">{active.over}</span>
      <h1 className="cc-page-title">{active.label}</h1>
      <p className="cc-page-desc">{active.desc}</p>
    </header>
  );
}

export function AdminConsole(): JSX.Element {
  // Flag-gated rail entries (flag-gating discipline: a dark surface must not
  // show its nav entry). agentProfile gates the 助手配置 section.
  const [agentProfileOn, setAgentProfileOn] = useState(false);
  useEffect(() => {
    void fetch("/healthz")
      .then((r) => (r.ok ? r.json() : null))
      .then((h: { features?: { agentProfile?: boolean } } | null) => {
        setAgentProfileOn(h?.features?.agentProfile === true);
      })
      .catch(() => undefined);
  }, []);
  // The admin browsing the console also counts as a live tab (#228 Phase 2).
  usePresence();
  const user = getUser();
  const onLogout = (): void => {
    clearToken();
    window.location.assign("/");
  };
  return (
    <ConsoleProvider>
      <div className="cc-shell">
        <nav className="cc-rail">
          <div className="cc-brand">
            <BrandLogo size={22} />
            <span className="cc-brand-name">控制台</span>
          </div>
          <div className="cc-rail-nav">
            {[
              ...useNav(),
              ...(agentProfileOn
                ? [
                    {
                      to: "/admin/assistants",
                      label: "助手配置",
                      over: "Assistants",
                      desc: "助手的身份、说明与发布",
                      Icon: IconUsers
                    }
                  ]
                : [])
            ].map(({ to, label, Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) => `cc-rail-link${isActive ? " active" : ""}`}
              >

                <Icon size={16} />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
          <span className="cc-rail-spacer" />
          {user !== null && (
            <div className="cc-rail-account">
              <span className="cc-rail-account-name" title={`${user.username} · ${user.tenant}`}>
                {user.username}
                <span className="cc-rail-account-tenant"> · {user.tenant}</span>
              </span>
              <button className="cc-rail-logout" type="button" onClick={onLogout}>
                退出
              </button>
            </div>
          )}
        </nav>
        <div className="cc-main">
          <Topbar />
          <div className="cc-content">
            <PageHead />
            <Outlet />
          </div>
        </div>
      </div>
    </ConsoleProvider>
  );
}
